<template>
  <div class="min-h-screen" :class="isDark ? 'bg-gray-900' : 'bg-white'">
    <div class="max-w-6xl mx-auto px-4 md:px-6 py-6">
      <!-- 顶部导航 -->
      <nav class="sticky top-16 z-20 mb-6 pb-3 backdrop-blur-sm"
           :class="isDark ? 'bg-gray-900/95' : 'bg-white/95'">
        <!-- 一级菜单 -->
        <div class="flex items-center gap-1 border-b"
             :class="isDark ? 'border-gray-700' : 'border-gray-200'">
          <button
            v-for="category in menuData"
            :key="category.key"
            @click="toggleCategory(category.key)"
            class="relative px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors"
            :class="activeCategory === category.key
              ? (isDark ? 'text-green-400' : 'text-green-600')
              : (isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800')"
          >
            {{ category.label }}
            <span v-if="activeCategory === category.key"
                  class="absolute left-3 right-3 -bottom-px h-0.5 rounded-full"
                  :class="isDark ? 'bg-green-400' : 'bg-green-500'"></span>
          </button>
          <button
            @click="navCollapsed = !navCollapsed"
            class="ml-auto px-2 py-2.5 transition-colors"
            :class="isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'"
            :title="navCollapsed ? '展开' : '收起'"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 transition-transform"
                 :class="navCollapsed ? '' : 'rotate-180'"
                 fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        <!-- 二级菜单 -->
        <div v-if="!navCollapsed && currentChildren.length"
             class="flex gap-1 pt-3 overflow-x-auto">
          <button
            v-for="item in currentChildren"
            :key="item.key"
            @click="selectTool(item.key)"
            class="px-3 py-1.5 rounded-md text-sm whitespace-nowrap transition-all flex items-center gap-1.5"
            :class="activeTool === item.key
              ? (isDark ? 'bg-green-500/15 text-green-400' : 'bg-green-50 text-green-700')
              : (isDark ? 'text-gray-400 hover:bg-gray-800 hover:text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800')"
          >
            <span>{{ item.icon }}</span>
            <span>{{ item.label }}</span>
          </button>
        </div>
      </nav>

      <!-- 内容区 -->
      <div class="rounded-xl border overflow-hidden"
           :class="isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'">
        <component :is="currentToolComponent" :key="activeTool" />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useTheme } from '../composables/useTheme'
import { useRoute } from 'vue-router'
import RandomStringGenerator from '../components/tools/RandomStringGenerator.vue'
import StringLength from '../components/tools/StringLength.vue'
import TimeConverter from '../components/tools/TimeConverter.vue'

export default {
  name: 'Tools',
  components: { RandomStringGenerator, StringLength, TimeConverter },
  setup() {
    const { isDark } = useTheme()
    const route = useRoute()

    const menuData = [
      {
        key: 'encode',
        label: '字符串',
        children: [
          { key: 'random-string', label: '随机生成字符串', icon: '🎲' },
          { key: 'string-length', label: '字符串长度', icon: '📏' }
        ]
      },
      {
        key: 'convert',
        label: '转换',
        children: [
          { key: 'time-convert', label: '时间转换', icon: '⏰' }
        ]
      }
    ]

    // 工具 -> 分类 映射
    const toolToCategory = {}
    menuData.forEach(cat => {
      cat.children.forEach(child => {
        toolToCategory[child.key] = cat.key
      })
    })

    const activeTool = ref('random-string')
    const activeCategory = ref(toolToCategory['random-string'] || 'encode')
    const navCollapsed = ref(false)

    // 从路由参数初始化
    onMounted(() => {
      const tool = route.query.tool
      if (tool && toolToCategory[tool]) {
        activeTool.value = tool
        activeCategory.value = toolToCategory[tool]
      }
    })

    const currentChildren = computed(() => {
      const cat = menuData.find(c => c.key === activeCategory.value)
      return cat ? cat.children : []
    })

    const toggleCategory = (key) => {
      if (activeCategory.value === key) {
        navCollapsed.value = !navCollapsed.value
      } else {
        activeCategory.value = key
        navCollapsed.value = false
      }
    }

    const selectTool = (key) => {
      activeTool.value = key
    }

    const toolMap = {
      'random-string': 'RandomStringGenerator',
      'string-length': 'StringLength',
      'time-convert': 'TimeConverter'
    }

    const currentToolComponent = computed(() => toolMap[activeTool.value] || 'RandomStringGenerator')

    return {
      isDark, menuData, activeCategory, activeTool, navCollapsed,
      currentChildren, toggleCategory, selectTool, currentToolComponent
    }
  }
}
</script>
